// PDF.js wrapper. Exposes two primitives:
//
//   pdfToTextLayout(buf): a best-effort plain-text reconstruction, useful
//     for sniffing or for any future generic-PDF parser.
//   extractPdfLines(buf): structured lines of text items with x/y/width.
//     This is what the bank-specific PDF parsers (BMO etc.) consume —
//     parsing from coordinates is more robust than parsing reconstructed
//     text, since PDF.js's text-item ordering is not bit-identical to
//     Poppler's pdftotext.
//
// Uses the legacy build of pdfjs-dist, which ships DOM shims and works in
// both Node (tests) and modern browsers — slightly larger bundle but
// avoids maintaining two code paths.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PDFTextItem = {
  str: string;
  // PDF.js transform matrix: [scaleX, skewY, skewX, scaleY, x, y].
  // We use entries [4] (x) and [5] (y) plus the item's width to place text.
  transform: number[];
  width: number;
  height: number;
  hasEOL?: boolean;
};

// Public shape returned by extractPdfLines — what bank-specific parsers consume.
export interface PdfItem {
  /** Left x in PDF user units. */
  x: number;
  /** Right x in PDF user units. */
  endX: number;
  /** Baseline y in PDF user units (top of page = higher y in PDF coords). */
  y: number;
  /** The text content of this item. May be a word, a phrase, or a single glyph. */
  str: string;
}
export interface PdfLine {
  /** Representative y for the line (top-down ordering across the document). */
  y: number;
  /** Page index (1-based) this line came from. */
  page: number;
  /** Items left-to-right within the line. */
  items: PdfItem[];
}

// Tunables for layout reconstruction. Defaults match what produces output
// close to `pdftotext -layout` on the synthetic BMO fixtures.
const DEFAULTS = {
  // Approximate width of one monospace-equivalent character in PDF user
  // units. Most bank statements use 9–10 pt fonts; 5 user-units/char gives
  // column positions within ±1 char of Poppler's output on our fixtures.
  charWidthUnits: 5,
  // Lines whose y-centers fall within this many user units are merged into
  // a single output line.
  lineGroupTolerance: 2.0,
  // If the next item starts within this many user units of where the
  // previous item ended, treat them as belonging to the same field and
  // insert at most a single space — not enough gap to be a new column.
  fieldGapUnits: 3.0,
};

interface ReconstructOptions {
  charWidthUnits?: number;
  lineGroupTolerance?: number;
  fieldGapUnits?: number;
}

// Reconstruct one PDF page's items into a layout-preserving text string.
// Items get grouped into lines by their y-coordinate, then each line's
// items are sorted by x and padded to character columns. Mirrors Poppler's
// `-layout` mode at a coarse level — fine enough that column-position
// regexes match within ±1 character.
function reconstructPageText(
  items: PDFTextItem[],
  opts: Required<ReconstructOptions>,
): string {
  if (items.length === 0) return "";
  // Bucket items by quantized y coordinate. PDF.js y grows upward, so we
  // negate to get top-to-bottom reading order.
  const lineBucket = new Map<number, PDFTextItem[]>();
  for (const it of items) {
    if (!it.str) continue;
    const y = it.transform[5];
    // Quantize within tolerance so 412.4 and 412.7 land in the same bucket.
    const key = Math.round(y / opts.lineGroupTolerance) * opts.lineGroupTolerance;
    let bucket = lineBucket.get(key);
    if (!bucket) {
      bucket = [];
      lineBucket.set(key, bucket);
    }
    bucket.push(it);
  }
  const lineKeys = [...lineBucket.keys()].sort((a, b) => b - a); // top to bottom

  const out: string[] = [];
  for (const key of lineKeys) {
    const bucket = lineBucket.get(key)!;
    bucket.sort((a, b) => a.transform[4] - b.transform[4]);
    let line = "";
    let prevEndX = -Infinity;
    for (const it of bucket) {
      const x = it.transform[4];
      const gapUnits = x - prevEndX;
      if (gapUnits <= opts.fieldGapUnits) {
        // Same field — items are visually adjacent. Single separator only
        // if the item's text doesn't already start with whitespace.
        if (line.length > 0 && !/^\s/.test(it.str)) line += " ";
      } else {
        // Real column gap. Pad to the item's absolute character column.
        const xCol = Math.round(x / opts.charWidthUnits);
        if (xCol > line.length) line += " ".repeat(xCol - line.length);
        else if (line.length > 0 && !line.endsWith(" ")) line += " ";
      }
      line += it.str;
      prevEndX = x + it.width;
    }
    out.push(line.trimEnd());
  }
  return out.join("\n");
}

// Hand pdfjs-dist a fresh Uint8Array with its own underlying buffer. pdfjs
// detaches the buffer it receives, so reusing the caller's bytes (or
// passing them to a second pdfjs call) explodes with
// "Cannot perform Construct on a detached ArrayBuffer". The
// `new Uint8Array(typedArray)` form COPIES the data into a new buffer;
// the `new Uint8Array(buffer, offset, length)` form does NOT — that's
// what bit us before. Note: also coerces Node Buffer → Uint8Array, which
// pdfjs rejects despite Buffer extending Uint8Array.
function copyBytesForPdfjs(data: ArrayBuffer | Uint8Array): Uint8Array {
  if (data instanceof ArrayBuffer) return new Uint8Array(data.slice(0));
  return new Uint8Array(data); // typedArray constructor → spec-mandated copy
}

// Pick the right pdfjs-dist build for the runtime. The legacy build works
// in both Node and browser without a worker, at the cost of ~200 KB extra.
// Lazy import so the test runner doesn't pay the cost until a PDF test runs.
async function getPdfJs() {
  const mod = await import("pdfjs-dist/legacy/build/pdf.mjs");
  return mod;
}

export async function pdfToTextLayout(
  data: ArrayBuffer | Uint8Array,
  opts: ReconstructOptions = {},
): Promise<string> {
  const merged: Required<ReconstructOptions> = {
    charWidthUnits: opts.charWidthUnits ?? DEFAULTS.charWidthUnits,
    lineGroupTolerance: opts.lineGroupTolerance ?? DEFAULTS.lineGroupTolerance,
    fieldGapUnits: opts.fieldGapUnits ?? DEFAULTS.fieldGapUnits,
  };
  const pdfjs = await getPdfJs();
  // pdfjs-dist always wants a worker source — even when we'd rather it run
  // in-process. Point it at the bundled worker file via import.meta.resolve
  // so it works both in Node (file:// URL) and in Vite (the bundler
  // rewrites this to a CDN/asset path at build time).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gw = (pdfjs as any).GlobalWorkerOptions;
  if (!gw.workerSrc) {
    const workerUrl = await import.meta.resolve!(
      "pdfjs-dist/legacy/build/pdf.worker.mjs",
    );
    gw.workerSrc = workerUrl;
  }

  // Two things going on with this dance:
  //  1) pdfjs-dist rejects Node Buffers (even though Buffer extends
  //     Uint8Array) with "Please provide binary data as `Uint8Array`,
  //     rather than `Buffer`." So we must wrap into a fresh Uint8Array.
  //  2) pdfjs-dist *detaches* the underlying ArrayBuffer once getDocument
  //     consumes it — subsequent calls with the same bytes throw
  //     "Cannot perform Construct on a detached ArrayBuffer". The
  //     `new Uint8Array(typedArray)` form COPIES the bytes (per the spec —
  //     unlike `new Uint8Array(buffer, offset, length)` which is a view),
  //     so each call gets its own buffer that pdfjs can detach freely.
  const u8 = copyBytesForPdfjs(data);
  const loadingTask = pdfjs.getDocument({
    data: u8,
    disableFontFace: true,
    useSystemFonts: false,
  });
  const doc = await loadingTask.promise;

  const pageTexts: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const tc = await page.getTextContent();
    const items = tc.items as PDFTextItem[];
    pageTexts.push(reconstructPageText(items, merged));
    page.cleanup();
  }
  await doc.cleanup();
  await doc.destroy();
  return pageTexts.join("\n\n");
}

// Extract structured lines (with item coordinates) from every page of the PDF.
// This is the primitive bank-specific PDF parsers consume — operating on
// (x, y, str) tuples is more robust than parsing reconstructed text, since
// PDF.js's item ordering doesn't always match pdftotext.
export async function extractPdfLines(
  data: ArrayBuffer | Uint8Array,
  opts: { lineGroupTolerance?: number } = {},
): Promise<PdfLine[]> {
  const tolerance = opts.lineGroupTolerance ?? DEFAULTS.lineGroupTolerance;
  const pdfjs = await getPdfJs();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gw = (pdfjs as any).GlobalWorkerOptions;
  if (!gw.workerSrc) {
    const workerUrl = await import.meta.resolve!(
      "pdfjs-dist/legacy/build/pdf.worker.mjs",
    );
    gw.workerSrc = workerUrl;
  }
  const u8 = copyBytesForPdfjs(data);
  const doc = await pdfjs.getDocument({
    data: u8,
    disableFontFace: true,
    useSystemFonts: false,
  }).promise;

  const out: PdfLine[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const tc = await page.getTextContent();
    const items = tc.items as PDFTextItem[];
    const bucket = new Map<number, PdfItem[]>();
    for (const it of items) {
      // Drop whitespace-only items — PDF.js emits inter-word gaps as
      // separate space-string items and they get in the way of every
      // downstream "is this item a date / number / text" check. The x/endX
      // of real items already tells us where the gaps are.
      if (!it.str || !it.str.trim()) continue;
      const y = it.transform[5];
      const key = Math.round(y / tolerance) * tolerance;
      let arr = bucket.get(key);
      if (!arr) {
        arr = [];
        bucket.set(key, arr);
      }
      arr.push({
        x: it.transform[4],
        endX: it.transform[4] + it.width,
        y,
        str: it.str,
      });
    }
    const keys = [...bucket.keys()].sort((a, b) => b - a); // top to bottom
    for (const k of keys) {
      const lineItems = bucket.get(k)!;
      lineItems.sort((a, b) => a.x - b.x);
      out.push({ y: k, page: p, items: lineItems });
    }
    page.cleanup();
  }
  await doc.cleanup();
  await doc.destroy();
  return out;
}
